import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { ALARM_SOUNDS, playAlarm, unlockAudio, LEVEL_COLORS } from "../lib/api";
import { saveActive, loadActive, clearActive } from "../lib/activeWorkoutPersistence";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, ArrowLeft, Check, Pause, Play as PlayIcon, Trophy, Share2, Sparkles, Layers, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { distanceLabel, metresToDisplay, displayToMetres } from "../lib/distance";

export default function ActiveWorkout() {
  const { workoutId } = useParams();
  const nav = useNavigate();
  const [wk, setWk] = useState(null);
  const [timerLeft, setTimerLeft] = useState(0);
  const [timerTotal, setTimerTotal] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  // Idle accumulation: phone locks / backgrounding while a workout is open
  // shouldn't count as workout time. This value is maintained by the global
  // visibilitychange listener in ActiveWorkoutBanner via localStorage, and
  // we mirror it into React state here so the on-screen Finish-workout
  // duration excludes it too. Persisted alongside the rest.
  const [idleMs, setIdleMs] = useState(0);
  const [alarmSound, setAlarmSound] = useState("beep");
  const [startedAt, setStartedAt] = useState(null);
  const [result, setResult] = useState(null);
  // Last completed session for this workout — drives the "pre-filled from
  // last time" hint that helps users confirm the numbers came from history.
  const [lastSession, setLastSession] = useState(null);

  const intervalRef = useRef(null);
  const { user } = useAuth();
  const distUnit = user?.distance_unit || "km";
  const [library, setLibrary] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addSearch, setAddSearch] = useState("");

  const addExerciseMidSession = (ex) => {
    const tracksDistance = ex.tracking_unit === "distance";
    setWk((prev) => ({
      ...prev,
      exercises: [...prev.exercises, {
        exercise_id: ex.exercise_id,
        name: ex.name,
        muscle_groups: ex.muscle_groups || [],
        tracking_unit: ex.tracking_unit || "reps",
        sets: tracksDistance
          ? [{ reps: 0, weight: 0, distance_m: 10, completed: false }, { reps: 0, weight: 0, distance_m: 10, completed: false }, { reps: 0, weight: 0, distance_m: 10, completed: false }]
          : [{ reps: 10, weight: 0, completed: false }, { reps: 10, weight: 0, completed: false }, { reps: 10, weight: 0, completed: false }],
        rest_seconds: prev?.rest_seconds || 60,
        notes: "",
        block_type: "single",
      }],
    }));
    setShowAdd(false);
    setAddSearch("");
    toast.success(`Added "${ex.name}"`);
  };

  // Orientation + wake lock: keep portrait & screen-on during the workout
  useEffect(() => {
    let wakeLock = null;
    let lockedOrientation = false;
    const acquire = async () => {
      if (!("wakeLock" in navigator) || wakeLock) return;
      try {
        wakeLock = await navigator.wakeLock.request("screen");
        // Browsers auto-release on visibility change — clear our ref when that happens
        // so a subsequent re-acquire on visibilitychange actually fires.
        wakeLock.addEventListener("release", () => { wakeLock = null; });
      } catch (_) { /* not supported */ }
    };
    (async () => {
      try {
        if (window.screen?.orientation?.lock) {
          await window.screen.orientation.lock("portrait");
          lockedOrientation = true;
        }
      } catch (_) { /* iOS doesn't support */ }
      await acquire();
    })();
    const onVisible = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (lockedOrientation) {
        try { window.screen.orientation.unlock(); } catch (_) {}
      }
      if (wakeLock) { try { wakeLock.release(); } catch (_) {} }
    };
  }, []);

  useEffect(() => {
    (async () => {
      // ----- RESUME PATH ----------------------------------------------
      // If we left this workout in progress (any time in the last 24h),
      // restore everything from localStorage and skip the template/last-
      // session merge. This is what powers the "Continue workout" flow:
      // the user clicks the floating banner, lands here, and sees their
      // ticked sets / typed weights / running timer exactly as they left.
      const saved = loadActive();
      if (saved && saved.workout_id === workoutId && saved.wk) {
        setWk(saved.wk);
        setLastSession(null); // banner is only for fresh starts
        setAlarmSound(saved.alarmSound || "beep");
        setStartedAt(saved.startedAt || Date.now());
        setIdleMs(saved.idleMs || 0);
        unlockAudio();
        return;
      }

      // ----- FRESH-START PATH -----------------------------------------
      const { data } = await api.get(`/workouts/${workoutId}`);
      // deep copy so we can mutate sets
      const copy = JSON.parse(JSON.stringify(data));
      // Pre-fill weights & reps from the last time the user did this workout.
      // We fetch the last session in parallel and merge per-exercise per-set:
      //   - match by exercise_id (NOT name, in case the user renames an
      //     exercise globally — match-by-id is stable across edits)
      //   - copy the last session's `weight`, `reps`, and `distance_m` into
      //     the corresponding template set INDEX. If the previous session
      //     had fewer sets than the template, the remaining template sets
      //     keep their defaults.
      //   - sets are always reset to `completed: false` because this is a
      //     new attempt.
      let lastSession = null;
      try {
        const res = await api.get(`/workouts/${workoutId}/last-session`);
        lastSession = res.data?.session || null;
      } catch (_) { /* no history yet — fine */ }
      copy.exercises = copy.exercises.map((e) => {
        const prevEx = lastSession?.exercises?.find(
          (px) => px.exercise_id === e.exercise_id,
        );
        const sets = e.sets.map((s, idx) => {
          const prev = prevEx?.sets?.[idx];
          if (!prev) return { ...s, completed: false };
          return {
            ...s,
            // Only copy numeric fields when the previous session actually
            // had a meaningful value. Falls back to the template default.
            weight: prev.weight != null ? prev.weight : s.weight,
            reps: prev.reps != null ? prev.reps : s.reps,
            ...(e.tracking_unit === "distance"
              ? { distance_m: prev.distance_m != null ? prev.distance_m : s.distance_m }
              : {}),
            completed: false,
          };
        });
        return { ...e, sets };
      });
      setWk(copy);
      setLastSession(lastSession); // expose to UI (small "from last time" hint)
      setAlarmSound(data.alarm_sound || "beep");
      setStartedAt(Date.now());
      // Belt-and-braces: explicitly resume the audio context now that the
      // user has tapped "Start workout" (this navigation IS a user gesture).
      unlockAudio();
    })();
  }, [workoutId]);

  // Persist the in-progress state to localStorage whenever it changes so a
  // navigation away (or a backgrounded PWA tab) can resume from exactly the
  // same place. Throttle implicit because React batches state updates.
  useEffect(() => {
    if (!wk || !startedAt) return;
    saveActive({ workout_id: workoutId, wk, startedAt, alarmSound, idleMs });
  }, [wk, startedAt, alarmSound, idleMs, workoutId]);

  // Pull the latest idleMs from localStorage whenever the persistence layer
  // fires a change event — covers the case where the visibilitychange
  // listener in ActiveWorkoutBanner accumulated idle time while this page
  // was hidden.
  useEffect(() => {
    const sync = () => {
      const saved = loadActive();
      if (saved && saved.workout_id === workoutId && typeof saved.idleMs === "number") {
        setIdleMs(saved.idleMs);
      }
    };
    window.addEventListener("gymgrizz:active-changed", sync);
    return () => window.removeEventListener("gymgrizz:active-changed", sync);
  }, [workoutId]);

  useEffect(() => {
    if (!timerRunning) return;
    intervalRef.current = setInterval(() => {
      setTimerLeft((t) => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          playAlarm(alarmSound);
          setTimerRunning(false);
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [timerRunning, alarmSound]);

  const toggleSet = (exIdx, setIdx) => {
    setWk((prev) => {
      const next = { ...prev };
      next.exercises = next.exercises.map((e, i) => i === exIdx ? {
        ...e,
        sets: e.sets.map((s, k) => k === setIdx ? { ...s, completed: !s.completed } : s),
      } : e);
      const wasDone = prev.exercises[exIdx].sets[setIdx].completed;
      if (!wasDone) {
        const rest = prev.exercises[exIdx].rest_seconds || prev.rest_seconds || 60;
        setTimerLeft(rest); setTimerTotal(rest); setTimerRunning(true);
      }
      return next;
    });
  };

  const updateSet = (exIdx, setIdx, field, val) => {
    setWk((prev) => ({
      ...prev,
      exercises: prev.exercises.map((e, i) => i === exIdx ? {
        ...e,
        sets: e.sets.map((s, k) => k === setIdx ? { ...s, [field]: val } : s),
      } : e),
    }));
  };

  const addSet = (exIdx) => {
    setWk((prev) => ({
      ...prev,
      exercises: prev.exercises.map((e, i) => {
        if (i !== exIdx) return e;
        const last = e.sets[e.sets.length - 1] || {};
        const newSet = e.tracking_unit === "distance"
          ? { reps: 0, weight: last.weight ?? 0, distance_m: last.distance_m ?? 10, completed: false }
          : { reps: last.reps ?? 10, weight: last.weight ?? 0, completed: false };
        return { ...e, sets: [...e.sets, newSet] };
      }),
    }));
  };

  const adjustTimer = (delta) => setTimerLeft((t) => Math.max(0, t + delta));

  const completeWorkout = async () => {
    // Exclude any time the PWA was backgrounded / phone was locked. Without
    // this, a workout left open overnight reports an 8h duration and skews
    // pace stats + PR comparisons.
    const duration = Math.max(
      0,
      Math.floor((Date.now() - startedAt - idleMs) / 1000),
    );
    try {
      const { data } = await api.post("/sessions/complete", {
        exercises: wk.exercises,
        duration_seconds: duration,
        workout_id: wk.workout_id,
        workout_name: wk.name,
        tz_offset: -new Date().getTimezoneOffset(),
      });
      setResult(data);
      // Workout is officially done — purge the in-progress save so the next
      // visit to /active/{id} starts fresh from the template.
      clearActive();
      // auto-post feed for PRs
      if (data.prs?.length) {
        for (const pr of data.prs) {
          await api.post("/feed", {
            text: `New PR on ${pr.exercise_name} — ${pr.weight}kg x ${pr.reps}`,
            kind: "pr",
            meta: pr,
          });
        }
      } else {
        await api.post("/feed", {
          text: `Completed "${wk.name}" — ${data.session.total_sets} sets, ${Math.round(data.session.total_weight_kg)}kg total`,
          kind: "workout",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save session");
    }
  };

  if (!wk) return <div className="p-6 text-neutral-400">Loading…</div>;

  const totalSets = wk.exercises.reduce((n, e) => n + e.sets.length, 0);
  const doneSets = wk.exercises.reduce((n, e) => n + e.sets.filter((s) => s.completed).length, 0);
  const progress = totalSets ? (doneSets / totalSets) * 100 : 0;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button
        data-testid="active-exit-btn"
        onClick={() => {
          // We persist progress automatically, so "Leave" just navigates
          // away — the workout will keep waiting via the floating
          // "Continue workout" banner. Only on an explicit Abandon do we
          // wipe the saved state.
          const abandon = window.confirm(
            "Your workout will be saved — tap CANCEL to leave and continue later, " +
            "or OK to ABANDON this workout (resets all sets you've ticked)."
          );
          if (abandon) {
            clearActive();
          }
          nav(-1);
        }}
        className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-3"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Exit
      </button>
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">ACTIVE SESSION</div>
      <h1 data-testid="active-workout-title" className="font-display text-4xl uppercase leading-none mt-1">{wk.name}</h1>
      {lastSession && (
        <div
          data-testid="prefill-hint"
          className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest border border-emerald-500/30 bg-emerald-500/5 text-emerald-400 px-2.5 py-1.5"
        >
          <History className="w-3 h-3" />
          Pre-filled from last session ·{" "}
          {new Date(lastSession.completed_at).toLocaleDateString("en", { month: "short", day: "numeric" })}
          {typeof lastSession.total_weight_kg === "number" && ` · ${Math.round(lastSession.total_weight_kg)}kg total`}
        </div>
      )}
      <div className="h-1 bg-neutral-900 mt-4"><div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} /></div>
      <div className="flex justify-between text-xs text-neutral-500 uppercase tracking-widest mt-1">
        <span>{doneSets}/{totalSets} sets</span>
        <span>{Math.round(progress)}%</span>
      </div>

      {/* Rest timer bar */}
      <div className={`sticky top-0 z-20 my-5 border ${timerRunning ? "border-blue-500 timer-pulse" : "border-neutral-800"} bg-neutral-950 p-4`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs tracking-widest uppercase text-neutral-400">Rest Timer</div>
            <div data-testid="timer-display" className={`font-display text-5xl leading-none ${timerRunning ? "text-blue-500" : "text-white"}`}>
              {String(Math.floor(timerLeft / 60)).padStart(2, "0")}:{String(timerLeft % 60).padStart(2, "0")}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => adjustTimer(-15)} data-testid="timer-minus" className="bg-transparent border-neutral-700 text-white rounded-none h-8">-15s</Button>
              <Button size="sm" variant="outline" onClick={() => adjustTimer(15)} data-testid="timer-plus" className="bg-transparent border-neutral-700 text-white rounded-none h-8">+15s</Button>
              <Button size="sm" onClick={() => setTimerRunning((r) => !r)} data-testid="timer-toggle" className="bg-blue-500 hover:bg-blue-600 rounded-none h-8">
                {timerRunning ? <Pause className="w-3 h-3" /> : <PlayIcon className="w-3 h-3" />}
              </Button>
            </div>
            <Select value={alarmSound} onValueChange={setAlarmSound}>
              <SelectTrigger data-testid="active-alarm-select" className="bg-neutral-900 border-neutral-800 rounded-none h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-neutral-900 border-neutral-800">{ALARM_SOUNDS.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {renderActiveBlocks(wk.exercises, { nav, toggleSet, updateSet, addSet, distUnit })}
      </div>

      <Button
        data-testid="active-add-exercise-btn"
        variant="outline"
        onClick={async () => {
          if (library.length === 0) {
            try { const { data } = await api.get("/exercises"); setLibrary(data); } catch (_) {}
          }
          setShowAdd(true);
        }}
        className="w-full bg-transparent border-dashed border-blue-500/40 text-white hover:border-blue-500 hover:bg-blue-500/5 rounded-none uppercase tracking-wider font-display h-11 mt-4"
      >
        <Plus className="w-4 h-4 mr-2 text-blue-500" /> Add exercise
      </Button>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Exercise</DialogTitle></DialogHeader>
          <Input data-testid="active-add-search" value={addSearch} onChange={(e) => setAddSearch(e.target.value)} placeholder="Search…" className="bg-neutral-900 border-neutral-800 rounded-none text-white" />
          <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-96 overflow-y-auto">
            {library.filter((x) => !addSearch || x.name.toLowerCase().includes(addSearch.toLowerCase())).slice(0, 100).map((ex) => (
              <button
                key={ex.exercise_id}
                data-testid={`active-add-${ex.exercise_id}`}
                onClick={() => addExerciseMidSession(ex)}
                className="w-full flex items-center justify-between p-3 hover:bg-neutral-900 text-left"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">{ex.name}</div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{ex.category} · {(ex.muscle_groups || []).join(", ")}</div>
                </div>
                <Plus className="w-4 h-4 text-blue-500 shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Button data-testid="finish-workout-btn" onClick={completeWorkout} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12 text-base mt-6">
        <Check className="w-4 h-4 mr-2" /> Finish Workout
      </Button>

      <Dialog open={!!result} onOpenChange={(o) => !o && (setResult(null), nav("/dashboard"))}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-3xl uppercase">Workout Complete</DialogTitle></DialogHeader>
          {result && <CelebrationCard result={result} wkName={wk.name} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderActiveBlocks(items, h) {
  const out = [];
  let i = 0;
  while (i < items.length) {
    const ex = items[i];
    if (ex.block_type === "superset" && ex.block_id) {
      const blockId = ex.block_id;
      const group = [];
      let j = i;
      while (j < items.length && items[j].block_id === blockId) {
        group.push({ ex: items[j], idx: j });
        j++;
      }
      out.push(<ActiveSupersetBlock key={`blk-${blockId}`} group={group} blockId={blockId} h={h} />);
      i = j;
    } else {
      out.push(<ActiveExerciseCard key={`ex-${i}`} ex={ex} ei={i} h={h} />);
      i++;
    }
  }
  return out;
}

function ActiveExerciseCard({ ex, ei, h }) {
  return (
    <div data-testid={`act-ex-${ei}`} className="border border-neutral-800 p-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-display text-xl uppercase truncate">{ex.name}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider truncate">{(ex.muscle_groups || []).join(", ")}</div>
        </div>
        <Button size="icon" variant="ghost" onClick={() => h.nav(`/exercises/${ex.exercise_id}`)} className="text-neutral-400 hover:text-blue-500" data-testid={`info-${ei}`}>
          <Sparkles className="w-4 h-4" />
        </Button>
      </div>
      <ActiveSetGrid ex={ex} ei={ei} h={h} />
      <Button size="sm" variant="outline" onClick={() => h.addSet(ei)} data-testid={`act-add-set-${ei}`} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider mt-2">
        <Plus className="w-3 h-3 mr-1" /> Set
      </Button>
    </div>
  );
}

function ActiveSupersetBlock({ group, blockId, h }) {
  const name = group[0].ex.superset_name || "Superset";
  return (
    <div data-testid={`act-superset-${blockId}`} className="border border-blue-500/60 bg-blue-500/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-blue-500 shrink-0" />
        <div className="text-[10px] tracking-[0.3em] uppercase text-blue-500 font-bold shrink-0">Superset</div>
        <div className="font-display text-xl uppercase truncate flex-1">{name}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">{group.length} exercises</div>
      </div>
      <div className="space-y-3">
        {group.map(({ ex, idx }, k) => (
          <div key={idx} data-testid={`act-ex-${idx}`} className="border border-neutral-800 bg-neutral-950 p-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-display text-base uppercase truncate">{String.fromCharCode(65 + k)}. {ex.name}</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{(ex.muscle_groups || []).join(", ")}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => h.nav(`/exercises/${ex.exercise_id}`)} className="text-neutral-400 hover:text-blue-500" data-testid={`info-${idx}`}>
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>
            <ActiveSetGrid ex={ex} ei={idx} h={h} compact />
            <Button size="sm" variant="outline" onClick={() => h.addSet(idx)} data-testid={`act-add-set-${idx}`} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider mt-2 h-7 text-[11px]">
              <Plus className="w-3 h-3 mr-1" /> Set
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActiveSetGrid({ ex, ei, h, compact = false }) {
  const cell = compact ? "h-8" : "h-9";
  const head = compact ? "text-[10px] mt-2" : "text-xs mt-3";
  const tracksDistance = ex.tracking_unit === "distance";
  const distLabel = distanceLabel(h.distUnit);
  const repsHeader = tracksDistance ? `Distance (${distLabel})` : "Reps";
  return (
    <>
      <div className={`grid grid-cols-[40px_1fr_1fr_40px] gap-2 tracking-widest uppercase text-neutral-500 mb-2 ${head}`}>
        <div>Set</div><div>{repsHeader}</div><div>Weight</div><div>Done</div>
      </div>
      {ex.sets.map((s, si) => (
        <div key={si} className={`grid grid-cols-[40px_1fr_1fr_40px] gap-2 items-center mb-2 p-2 ${s.completed ? "bg-blue-500/10 border-l-2 border-blue-500" : ""}`}>
          <div className="text-neutral-400 text-sm pl-1">{si + 1}</div>
          {tracksDistance ? (
            <Input
              data-testid={`act-distance-${ei}-${si}`}
              type="number" min={0} step={1}
              value={metresToDisplay(s.distance_m, h.distUnit)}
              onChange={(e) => h.updateSet(ei, si, "distance_m", displayToMetres(e.target.value, h.distUnit))}
              className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`}
            />
          ) : (
            <Input data-testid={`act-reps-${ei}-${si}`} type="number" min={0} value={s.reps} onChange={(e) => h.updateSet(ei, si, "reps", Number(e.target.value))} className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`} />
          )}
          <Input data-testid={`act-weight-${ei}-${si}`} type="number" min={0} step={0.5} value={s.weight} onChange={(e) => h.updateSet(ei, si, "weight", Number(e.target.value))} className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`} />
          <Checkbox data-testid={`act-check-${ei}-${si}`} checked={s.completed} onCheckedChange={() => h.toggleSet(ei, si)} className="border-neutral-700 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500" />
        </div>
      ))}
    </>
  );
}

function CelebrationCard({ result, wkName }) {
  const { session, fun_comparison, prs, level, leveled_up, points_earned } = result;
  const levelColor = LEVEL_COLORS[level.name];
  const share = async () => {
    let gymTag = "";
    let buddyTag = "";
    try {
      const me = await api.get("/auth/me");
      if (me.data.gym_name) gymTag = ` @ ${me.data.gym_name}`;
      const b = await api.get("/buddies");
      const names = (b.data.accepted || []).map((x) => x.from_user_id === me.data.user_id ? x.to_name : x.from_name);
      if (names.length) buddyTag = ` w/ ${names.join(", ")}`;
    } catch {}
    const text = `I just crushed "${wkName}" on GYM with GRIZZ! ${fun_comparison}${gymTag}${buddyTag} 💪`;
    const url = window.location.origin;
    if (navigator.share) navigator.share({ title: "GYM with GRIZZ", text, url }).catch(() => {});
    else {
      navigator.clipboard.writeText(text + " " + url);
      toast.success("Copied to clipboard");
    }
  };
  return (
    <div className="space-y-4 text-center fade-up">
      <Trophy className="w-16 h-16 text-blue-500 mx-auto" />
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">TOTAL LIFTED</div>
        <div className="font-display text-5xl text-white">{Math.round(session.total_weight_kg)} KG</div>
        <div className="text-neutral-300 text-sm mt-2 italic">"{fun_comparison}"</div>
      </div>
      <div className="grid grid-cols-3 gap-2 border-y border-neutral-800 py-3">
        <div><div className="text-xs uppercase tracking-wider text-neutral-500">Sets</div><div className="font-display text-xl">{session.total_sets}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-neutral-500">Reps</div><div className="font-display text-xl">{session.total_reps}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-neutral-500">Time</div><div className="font-display text-xl">{Math.floor(session.duration_seconds / 60)}m</div></div>
      </div>
      {leveled_up && (
        <div className="border border-blue-500 p-3 fade-up">
          <div className="text-xs uppercase tracking-widest text-blue-500">LEVEL UP</div>
          <div className="font-display text-3xl uppercase" style={{ color: levelColor }}>{level.name}</div>
        </div>
      )}
      {prs?.length > 0 && (
        <div className="border border-blue-500 p-3">
          <div className="text-xs uppercase tracking-widest text-blue-500">Personal Records</div>
          <div className="space-y-1 mt-2 text-sm">
            {prs.map((p, i) => <div key={i} className="text-white">{p.exercise_name} — {p.broke.join(" & ")} PR</div>)}
          </div>
        </div>
      )}
      <div className="text-xs text-neutral-500 uppercase tracking-wider">+{points_earned} points earned</div>
      <div className="flex gap-2">
        <Button data-testid="share-btn" onClick={share} className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display">
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
      </div>
    </div>
  );
}
